import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

const STACK_SX = { px: 1, py: 0.5 }
const SLIDER_SX = { py: 1 }
const LABEL_SX = { color: 'text.primary' }
const VALUE_SX = { color: 'text.secondary' }
const LABEL_GROUP_SX = { display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }
const INFO_ICON_SX = { color: 'text.secondary', cursor: 'help' }

// `tooltip` is opt-in: what the slider does to the map is worth a sentence, but
// every existing consumer renders exactly as before without one.
const MarkerOffsetControl = ({ value, onChange, min = 0, max = 15, label = 'Offset', suffix = 'm', sx, tooltip }) => {
  const [internalValue, setInternalValue] = useState(value)

  useEffect(() => {
    setInternalValue(value)
  }, [value])

  const handleChange = useCallback((_, newValue) => {
    setInternalValue(newValue)
  }, [])

  const handleChangeCommitted = useCallback(
    (_, newValue) => {
      onChange(newValue)
    },
    [onChange]
  )

  const paperSx = useMemo(
    () => theme => {
      const { glass } = theme.palette
      return {
        p: 0.5,
        mt: 1,
        mr: 1,
        minWidth: 140,
        ...(glass && {
          bgcolor: glass.surface,
          border: `1px solid ${glass.border}`,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          boxShadow: glass.shadowHeavy,
        }),
        ...sx,
      }
    },
    [sx]
  )

  return (
    <Paper elevation={2} sx={paperSx}>
      <Stack direction="column" spacing={0} sx={STACK_SX}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Box sx={LABEL_GROUP_SX}>
            <Typography sx={LABEL_SX} variant="subtitle2">
              {label}
            </Typography>
            {tooltip && (
              <Tooltip arrow placement="top" title={tooltip}>
                {/* aria-label, not a describedby: the icon IS the affordance, and the
                    slider already names itself off `label`. */}
                <InfoOutlinedIcon aria-label={tooltip} fontSize="inherit" sx={INFO_ICON_SX} />
              </Tooltip>
            )}
          </Box>
          <Typography sx={VALUE_SX} variant="caption">
            {internalValue}
            {suffix}
          </Typography>
        </Box>
        <Slider
          aria-label={label}
          max={max}
          min={min}
          size="small"
          sx={SLIDER_SX}
          value={internalValue}
          valueLabelDisplay="auto"
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
        />
      </Stack>
    </Paper>
  )
}

MarkerOffsetControl.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  label: PropTypes.string,
  suffix: PropTypes.string,
  sx: PropTypes.object,
  tooltip: PropTypes.string,
}

export default MarkerOffsetControl
